import { useState } from "react";
import type { TaggedRiskRow } from "@/lib/api/riskV3";
import { ReleaseCard } from "./ReleaseCard";
import { BrokerageCard } from "./BrokerageCard";
import { OpenOfferCard } from "./OpenOfferCard";
import { formatNumberToDisplay } from "@/lib/api/common";
import Decimal from "decimal.js";
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from "@/components/ui/collapsible";
import { ChevronDown, ChevronRight } from "lucide-react";

// ============================================================================
// Breakdown category definitions (mirrors SummaryDashboard)
// ============================================================================

type CardType = "release" | "brokerage" | "open_offer";

interface BreakdownCategory {
  label: string;
  order: number;
  categoryType: "Futures" | "Physical";
  cardType: CardType;
  filter: (row: TaggedRiskRow) => boolean;
}

const BREAKDOWN_CATEGORIES: BreakdownCategory[] = ([
  {
    label: "Futures HTA",
    categoryType: "Futures",
    cardType: "release",
    filter: (row) => row.meta.is_hta === true && row.meta.contributes_to_futures_hedging === true,
  },
  {
    label: "Futures OTC",
    categoryType: "Futures",
    cardType: "release",
    filter: (row) => row.meta.is_otc === true && row.meta.contributes_to_futures_hedging === true,
  },
  {
    label: "Futures Brokerage Positions",
    categoryType: "Futures",
    cardType: "brokerage",
    filter: (row) => !!row.meta.brokerage_type && row.meta.is_hedgeable === true && row.meta.contributes_to_futures_hedging && row.meta.is_brokerage_position,
  },
  {
    label: "Futures Brokerage Open Offers",
    categoryType: "Futures",
    cardType: "open_offer",
    filter: (row) => !!row.meta.brokerage_type && row.meta.is_hedgeable === true && row.meta.is_open_offer,
  },
  {
    label: "Futures Brokerage P&S",
    categoryType: "Futures",
    cardType: "brokerage",
    filter: (row) => !!row.meta.brokerage_type && row.meta.is_hedgeable === true && row.meta.is_realized,
  },
  {
    label: "Futures Direct Sales",
    categoryType: "Futures",
    cardType: "release",
    filter: (row) => row.meta.is_direct_sale && row.meta.contributes_to_futures_hedging,
  },
  {
    label: "Futures DPQ",
    categoryType: "Physical",
    cardType: "release",
    filter: (row) => row.meta.is_dpq && row.meta.contributes_to_futures_hedging,
  },
  {
    label: "Physical Basis",
    categoryType: "Physical",
    cardType: "release",
    filter: (row) => row.meta.contributes_to_physical_hedging && row.meta.physical_contribution_type === "basis" && row.meta.is_basis_only === true,
  },
  {
    label: "Physical OTC",
    categoryType: "Physical",
    cardType: "release",
    filter: (row) => row.meta.is_otc === true && row.meta.is_basis_only === true,
  },
  {
    label: "Physical Cash",
    categoryType: "Physical",
    cardType: "release",
    filter: (row) => row.meta.is_cash === true,
  },
  {
    label: "Physical Direct Sales",
    categoryType: "Physical",
    cardType: "release",
    filter: (row) => row.meta.is_direct_sale && row.meta.contributes_to_physical_hedging,
  },
  {
    label: "Physical DPQ",
    categoryType: "Physical",
    cardType: "release",
    filter: (row) => row.meta.is_dpq && row.meta.contributes_to_physical_hedging,
  },
  {
    label: "DPQ Unset",
    categoryType: "Physical",
    cardType: "release",
    filter: (row) => (row.meta.is_dpq || row.meta.is_dp) && (!row.meta.contributes_to_futures_hedging && !row.meta.contributes_to_physical_hedging),
  },
] as Omit<BreakdownCategory, "order">[]).map((item, index) => ({ ...item, order: index + 1 }));

// ============================================================================
// Grouping logic
// ============================================================================

interface CategoryGroup {
  label: string;
  order: number;
  categoryType: "Futures" | "Physical";
  cardType: CardType;
  rows: TaggedRiskRow[];
  totalQuantity: Decimal;
}

interface CommodityGroup {
  commodity: string;
  commodityDisplayName: string;
  categories: CategoryGroup[];
}

function groupRowsByCommodityAndCategory(rows: TaggedRiskRow[]): CommodityGroup[] {
  // commodity → category label → rows
  const commodityMap = new Map<string, Map<string, TaggedRiskRow[]>>();

  for (const row of rows) {
    const commodity = row.meta.commodity_group || "Unknown";

    if (!commodityMap.has(commodity)) {
      commodityMap.set(commodity, new Map());
    }
    const catMap = commodityMap.get(commodity)!;

    for (const category of BREAKDOWN_CATEGORIES) {
      if (category.filter(row)) {
        if (!catMap.has(category.label)) {
          catMap.set(category.label, []);
        }
        catMap.get(category.label)!.push(row);
      }
    }
  }

  // Build sorted structure
  const result: CommodityGroup[] = [];

  const sortedCommodities = [...commodityMap.keys()].sort((a, b) => a.localeCompare(b));

  for (const commodity of sortedCommodities) {
    const catMap = commodityMap.get(commodity)!;
    const categories: CategoryGroup[] = [];

    for (const category of BREAKDOWN_CATEGORIES) {
      const catRows = catMap.get(category.label);
      if (!catRows || catRows.length === 0) continue;

      let totalQuantity = new Decimal(0);
      for (const r of catRows) {
        totalQuantity = totalQuantity.plus(r.quantity ?? 0);
      }

      categories.push({
        label: category.label,
        order: category.order,
        categoryType: category.categoryType,
        cardType: category.cardType,
        rows: catRows,
        totalQuantity,
      });
    }

    if (categories.length === 0) continue;

    // Sort by categoryType (Futures first), then order
    categories.sort((a, b) => {
      if (a.categoryType !== b.categoryType) {
        return a.categoryType === "Futures" ? -1 : 1;
      }
      return a.order - b.order;
    });

    // Derive display name from first row's meta
    const displayName = catMap.values().next().value?.[0]?.meta.commodity_display_name ?? commodity;

    result.push({
      commodity,
      commodityDisplayName: displayName,
      categories,
    });
  }

  return result;
}

// ============================================================================
// Card renderer
// ============================================================================

function renderCard(row: TaggedRiskRow, cardType: CardType, customerNameMap: Record<string, string>) {
  switch (cardType) {
    case "release":
      return <ReleaseCard row={row} customerNameMap={customerNameMap} />;
    case "brokerage":
      return <BrokerageCard row={row} customerNameMap={customerNameMap} />;
    case "open_offer":
      return <OpenOfferCard row={row} customerNameMap={customerNameMap} />;
  }
}

// ============================================================================
// Component
// ============================================================================

interface DetailedCardsSectionProps {
  rows: TaggedRiskRow[];
  customerNameMap: Record<string, string>;
  filteredCustomerIds: string[];
}

function CollapsibleSection({ sectionKey, defaultOpen, children, trigger }: {
  sectionKey: string;
  defaultOpen: boolean;
  children: React.ReactNode;
  trigger: (isOpen: boolean) => React.ReactNode;
}) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger className="flex items-center gap-2 cursor-pointer select-none w-full">
        {trigger(isOpen)}
      </CollapsibleTrigger>
      <CollapsibleContent>
        {children}
      </CollapsibleContent>
    </Collapsible>
  );
}

export function DetailedCardsSection({ rows, customerNameMap, filteredCustomerIds }: DetailedCardsSectionProps) {
  if (filteredCustomerIds.length === 0) {
    return null;
  }

  const grouped = groupRowsByCommodityAndCategory(rows);

  if (grouped.length === 0) {
    return (
      <div className="text-sm text-gray-500">No detailed rows found for filtered customers.</div>
    );
  }

  return (
    <div className="space-y-4">
      {grouped.map((commodityGroup) => (
        <CollapsibleSection
          key={commodityGroup.commodity}
          sectionKey={commodityGroup.commodity}
          defaultOpen={true}
          trigger={(isOpen) => (
            <>
              {isOpen ? (
                <ChevronDown className="h-4 w-4 text-gray-500" />
              ) : (
                <ChevronRight className="h-4 w-4 text-gray-500" />
              )}
              <h3 className="text-base font-semibold">{commodityGroup.commodityDisplayName}</h3>
            </>
          )}
        >
          <div className="mt-3 space-y-3 pl-6">
            {commodityGroup.categories.map((category) => (
              <CollapsibleSection
                key={`${commodityGroup.commodity}::${category.label}`}
                sectionKey={`${commodityGroup.commodity}::${category.label}`}
                defaultOpen={true}
                trigger={(isOpen) => (
                  <>
                    {isOpen ? (
                      <ChevronDown className="h-3.5 w-3.5 text-gray-400" />
                    ) : (
                      <ChevronRight className="h-3.5 w-3.5 text-gray-400" />
                    )}
                    <h4 className="text-sm font-medium text-gray-700">{category.label}</h4>
                    <span className="text-xs text-muted-foreground">
                      {category.rows.length} {category.rows.length === 1 ? "row" : "rows"}
                      {" · "}
                      {formatNumberToDisplay(category.totalQuantity.toNumber())} total
                    </span>
                  </>
                )}
              >
                <div className="mt-2 max-h-[500px] overflow-y-auto space-y-2 pr-1">
                  {category.rows.map((row) => (
                    <div key={row.id}>
                      {renderCard(row, category.cardType, customerNameMap)}
                    </div>
                  ))}
                </div>
              </CollapsibleSection>
            ))}
          </div>
        </CollapsibleSection>
      ))}
    </div>
  );
}
